import React from "react";
//#region redux
//#endregion
//#region  types
import { Amount } from "models/utilTypes/currencyType";
import {
  WintSuperkollAPIModelHoldingRegisters as HoldingRegistersApiModel,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { TableEnums } from "models/enums";
import { HoldingRegisterFormTypes } from "models/formTypes";
import { AssetsListViewProps } from "models/propTypes/holdingRegisterPropTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
import { DateConstants } from "utils/constants";
import { YearAndMonth } from "models/propTypes/commonPropTypes";
//#endregion
//#region Components
import { CurrencyValueFormatter, TableAsync, useFilterContext } from "components/common";
import AssetStatusTags from "./statusTags";
//#endregion
import { useHoldingRegisterService } from "serviceHooks";

const AssetsList: React.ForwardRefRenderFunction<TableHandles, AssetsListViewProps> = (props, ref) => {
  const holdingRegisterService = useHoldingRegisterService();
  const { exportFileName, holdingRegisterId } = props;
  const { filter } = useFilterContext<HoldingRegisterFormTypes.HoldingRegisterAssetsFilter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) =>
      holdingRegisterService.filterAssets(holdingRegisterId, baseFilter, filter),
    [filter, holdingRegisterId]
  );

  return (
    <TableAsync<HoldingRegistersApiModel.Asset>
      tableRef={ref}
      properties={{
        serialNumber: {
          description: TranslationKeys.Common.Number,
          maxWidth: 45,
          pdfColumnwidth: 45,
        },
        description: {
          description: TranslationKeys.Common.Description,
          noWrap: true,
          pdfColumnwidth: 100,
        },
        bookingAccount: {
          description: TranslationKeys.HoldingRegister.ExpenseAccount,
          noWrap: true,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            asset.bookingAccount
              ? `${asset.bookingAccount}`.concat(asset.bookingAccountName ? ` - ${asset.bookingAccountName}` : "")
              : undefined,
        },
        startMonth: {
          description: TranslationKeys.HoldingRegister.AcquisitionDate,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            asset.startMonth
              ? new YearAndMonth(asset.startMonth.year, asset.startMonth.month, DateConstants.MonthYearShortConfig)
              : undefined,
          noWrap: true,
          pdfColumnwidth: 50,
        },
        terminationMonth: {
          description: TranslationKeys.HoldingRegister.Expires,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            asset.terminationMonth
              ? new YearAndMonth(
                  asset.terminationMonth.year,
                  asset.terminationMonth.month,
                  DateConstants.MonthYearShortConfig
                )
              : undefined,
          alignment: "right",
          noWrap: true,
          pdfColumnwidth: 50,
        },
        purchaseValue: {
          description: TranslationKeys.HoldingRegister.PurchaseValue,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            new Amount(asset.purchaseValue?.type, asset.purchaseValue?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.Asset) => (
            <CurrencyValueFormatter cellValue={summary.purchaseValue ? [summary.purchaseValue] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.Asset) =>
            summary.purchaseValue ? [summary.purchaseValue] : [],
          alignment: "right",
          noWrap: true,
        },
        terminalValue: {
          description: TranslationKeys.HoldingRegister.ResidualValue,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            new Amount(asset.terminalValue?.type, asset.terminalValue?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.Asset) => (
            <CurrencyValueFormatter cellValue={summary.terminalValue ? [summary.terminalValue] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.Asset) =>
            summary.terminalValue ? [summary.terminalValue] : [],
          alignment: "right",
          noWrap: true,
        },
        valuePerMonth: {
          description: TranslationKeys.HoldingRegister.DeptPerMonth,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            new Amount(asset.valuePerMonth?.type, asset.valuePerMonth?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.Asset) => (
            <CurrencyValueFormatter cellValue={summary.valuePerMonth ? [summary.valuePerMonth] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.Asset) =>
            summary.valuePerMonth ? [summary.valuePerMonth] : [],
          alignment: "right",
          noWrap: true,
        },
        accumulatedDepreciation: {
          description: TranslationKeys.HoldingRegister.AlasDepreciation,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            new Amount(asset.accumulatedDepreciation?.type, asset.accumulatedDepreciation?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.Asset) => (
            <CurrencyValueFormatter
              cellValue={summary.accumulatedDepreciation ? [summary.accumulatedDepreciation] : []}
            />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.Asset) =>
            summary.accumulatedDepreciation ? [summary.accumulatedDepreciation] : [],
          alignment: "right",
          noWrap: true,
        },
        bookedValue: {
          description: TranslationKeys.HoldingRegister.BookValue,
          formatter: (asset: HoldingRegistersApiModel.Asset) =>
            new Amount(asset.bookedValue?.type, asset.bookedValue?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.Asset) => (
            <CurrencyValueFormatter cellValue={summary.bookedValue ? [summary.bookedValue] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.Asset) =>
            summary.bookedValue ? [summary.bookedValue] : [],
          alignment: "right",
          noWrap: true,
        },
        quotient: {
          description: TranslationKeys.HoldingRegister.DeptDegree,
          formatter: (asset: HoldingRegistersApiModel.Asset) => {
            const quotientPercentage = asset.quotient ? Math.floor(asset.quotient * 100) : 0;
            return `${quotientPercentage}%`;
          },
          noWrap: true,
          alignment: "right",
          disableSort: true,
          pdfColumnwidth: 45,
          classification: "number",
        },
        depreciates: {
          description: TranslationKeys.HoldingRegister.Depreciation,
          formatter: (asset: HoldingRegistersApiModel.Asset) => <AssetStatusTags status={asset.depreciates} />,
          valueToPrint: (asset: HoldingRegistersApiModel.Asset) =>
            holdingRegisterService.getValueToPrintForAssetTag(asset.depreciates),
          alignment: "center",
          noWrap: true,
          pdfColumnwidth: 45,
        },
        isActive: {
          description: TranslationKeys.Common.Active,
          formatter: (asset: HoldingRegistersApiModel.Asset) => <AssetStatusTags status={asset.isActive} />,
          valueToPrint: (asset: HoldingRegistersApiModel.Asset) =>
            holdingRegisterService.getValueToPrintForAssetTag(asset.isActive),
          alignment: "center",
          noWrap: true,
          pdfColumnwidth: 45,
        },
        isDeleted: {
          description: TranslationKeys.HoldingRegister.Removed,
          formatter: (asset: HoldingRegistersApiModel.Asset) => <AssetStatusTags status={asset.isDeleted} />,
          valueToPrint: (asset: HoldingRegistersApiModel.Asset) =>
            holdingRegisterService.getValueToPrintForAssetTag(asset.isDeleted),
          alignment: "center",
          noWrap: true,
          pdfColumnwidth: 45,
        },
      }}
      pagination={"advanced"}
      maxRowCount={10}
      hidePaginationIfSinglePage
      includeSummary
      tableId={TableEnums.TableType.HoldingRegisterAssetsList}
      filter={filterFunc}
      exportFileName={exportFileName}
      pdfHeaderTextSmall
    />
  );
};

export default React.forwardRef(AssetsList);
